import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Rocket, Youtube, StickyNote, FlaskConical, BookOpen, Loader2, Trash2, Network, CheckCircle2, AlertTriangle, Upload } from "lucide-react";
import { toast } from "sonner";

const SOURCE_ROWS = [
  { key: "videos", label: "Lecture videos", Icon: Youtube, to: "/app/learn" },
  { key: "notes", label: "Notes", Icon: StickyNote, to: "/app/notes" },
  { key: "papers", label: "Papers", Icon: FlaskConical, to: "/app/documents" },
  { key: "turbolearn", label: "TurboLearn imports", Icon: BookOpen, to: "/app/turbolearn" },
];

function YouTubeBatch({ onDone }) {
  const [urls, setUrls] = useState("");
  const [course, setCourse] = useState("");
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const startBatch = async () => {
    try {
      const { data } = await api.post("/ingest/youtube-batch", { urls, course });
      setJob(data); setUrls("");
      timer.current = setInterval(async () => {
        const { data: j } = await api.get(`/ingest/jobs/${data.job_id}`);
        setJob(j);
        if (j.status === "finished") { clearInterval(timer.current); onDone(); toast.success(`Connected ${j.items.filter(i => i.status === "ready").length}/${j.total} videos`); }
      }, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };

  return (
    <div className="aria-card p-5 mb-6" data-testid="youtube-batch">
      <div className="flex items-center gap-2 mb-2"><Youtube size={15} className="text-[var(--aria-primary)]" /><span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">Connect lectures in bulk</span></div>
      <p className="text-sm text-[var(--aria-text-muted)] mb-3">Paste up to 25 YouTube lecture links, one per line. ARIA pulls the real transcript, chunks it by timestamp and extracts concepts.</p>
      <textarea value={urls} onChange={e => setUrls(e.target.value)} rows={4} placeholder={"https://www.youtube.com/watch?v=…\nhttps://youtu.be/…"} data-testid="youtube-batch-input"
        className="w-full px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm font-mono" />
      <div className="flex gap-2 mt-2">
        <input value={course} onChange={e => setCourse(e.target.value)} placeholder="Course (optional)" data-testid="youtube-batch-course"
          className="flex-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-sm" />
        <button onClick={startBatch} disabled={!urls.trim() || job?.status === "running"} data-testid="youtube-batch-start" className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
          {job?.status === "running" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Connect
        </button>
      </div>
      {job && (
        <div className="mt-3 text-xs" data-testid="youtube-batch-progress">
          <div className="text-[var(--aria-text-muted)] mb-1">{job.done}/{job.total} processed{job.status === "finished" ? " · done" : "…"}</div>
          <ul className="space-y-1">
            {job.items.map((it, i) => (
              <li key={`${it.url}-${i}`} className="flex items-center gap-2">
                {it.status === "ready" ? <CheckCircle2 size={12} className="text-emerald-400" /> : <AlertTriangle size={12} className="text-amber-400" />}
                <span className="truncate">{it.title || it.url}</span>{it.error && <span className="text-amber-400 truncate">— {it.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CourseSetup() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = async () => { try { const { data } = await api.get("/ingest/status"); setStatus(data); } catch (err) { console.error("[ARIA]", err); } };
  useEffect(() => { load(); }, []);

  const resetDemo = async () => {
    if (!confirm("Remove the seeded thermodynamics demo (note, video, TurboLearn import, paper) and the mastery derived only from it?")) return;
    setBusy("reset");
    try { const { data } = await api.post("/ingest/reset-demo"); toast.success(`Removed ${data.removed} demo sources`); load(); }
    catch { toast.error("Reset failed"); } finally { setBusy(null); }
  };
  const rebuild = async () => {
    setBusy("graph");
    try { const { data } = await api.post("/ingest/rebuild-graph"); toast.success(`Graph rebuilt from ${data.sources_used} sources · ${data.edges} edges`); load(); }
    catch { toast.error("Rebuild failed"); } finally { setBusy(null); }
  };

  if (!status) return null;
  const realTotal = Object.values(status.real).reduce((a, b) => a + b, 0);

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-1"><Rocket size={22} className="text-[var(--aria-primary)]" /><h1 className="font-display text-3xl">Course setup</h1></div>
        <p className="text-sm text-[var(--aria-text-muted)] mb-6">Bring in your real material, retire the demo, and rebuild the knowledge graph from what you actually study.</p>

        <div className="aria-card p-5 mb-6" data-testid="ingest-status">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold">What's connected</span>
            <span className="aria-pill text-[10px]" data-testid="ingest-real-total">{realTotal} real · {status.concepts} concepts · {status.edges} links</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {SOURCE_ROWS.map(({ key, label, Icon, to }) => (
              <button key={key} onClick={() => navigate(to)} data-testid={`ingest-row-${key}`} className="flex items-center justify-between px-3 py-2 rounded-md bg-[var(--aria-surface-2)] text-sm hover:ring-1 hover:ring-[var(--aria-primary)] text-left">
                <span className="inline-flex items-center gap-2"><Icon size={14} className="text-[var(--aria-primary)]" /> {label}</span>
                <span className="text-xs text-[var(--aria-text-muted)]">{status.real[key]} real{status.demo[key] ? ` · ${status.demo[key]} demo` : ""}</span>
              </button>
            ))}
          </div>
        </div>

        <YouTubeBatch onDone={load} />

        <div className="aria-card p-5 mb-6" data-testid="ingest-actions">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--aria-text-muted)] font-semibold mb-3">Housekeeping</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={rebuild} disabled={!!busy} data-testid="rebuild-graph-button" className="btn px-4 py-2 text-sm border border-[var(--aria-border)] hover:border-[var(--aria-primary)] inline-flex items-center gap-2 disabled:opacity-50">
              {busy === "graph" ? <Loader2 size={14} className="animate-spin" /> : <Network size={14} />} Rebuild knowledge graph
            </button>
            <button onClick={resetDemo} disabled={!!busy || !status.demo_present} data-testid="reset-demo-button" className="btn px-4 py-2 text-sm border border-red-500/40 text-red-300 hover:border-red-400 inline-flex items-center gap-2 disabled:opacity-40">
              {busy === "reset" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} {status.demo_present ? "Remove demo data" : "Demo data removed"}
            </button>
          </div>
          <p className="text-xs text-[var(--aria-text-muted)] mt-3">Removing the demo drops the seeded thermodynamics note, lecture, TurboLearn import and paper, plus any concept whose only evidence came from them. Interview history and your own sources are untouched.</p>
        </div>
      </div>
    </div>
  );
}
